import React from "react";
// COMPONENTS
import { CircularProgress } from "@material-ui/core";
import LazyImage from "Components/Commons/LazyImage/LazyImage";
// HELPERS
import { IMAGES } from "Helpers";
// STYLES
import { LoaderWrapper } from "./GameLoader.style";

/**
 * Branded, full-screen loading state used as the `fallback` for lazily
 * loaded game routes (Science, Civics, and any other subject).
 *
 * Why this exists:
 *   The game routes are code-split with @loadable/component. Without a
 *   fallback, loadable renders `null` while the game's JS chunk downloads.
 *   Because the previous screen is already unmounted and the global <body>
 *   has no background colour, that gap showed up as a brief WHITE FLASH.
 *
 * This component fills the viewport with the game's own background
 * (compressed → sharp via <LazyImage />) plus a centred spinner, so the
 * transition Dashboard → loader → game is seamless and never blank.
 *
 * Usage:
 *   // Subject-specific background (recommended — most seamless transition)
 *   LazyLoader(() => import("..."), {
 *     fallback: <GameLoader
 *       compressedImage={IMAGES.CIVICS.BACKGROUND_COMPRESSED}
 *       image={IMAGES.CIVICS.BACKGROUND}
 *     />
 *   })
 *
 *   // Default (Science background) — kept for backward-compat
 *   LazyLoader(() => import("..."), { fallback: <GameLoader /> })
 *
 * Everything it needs (LazyImage, IMAGES, MUI, styled) lives in the main
 * bundle, so it renders immediately and does NOT depend on the chunk it is
 * waiting for.
 */
const GameLoader = ({
	message = "Loading game...",
	compressedImage = IMAGES.SCIENCE.COMPRESSED_COMMON_BACKGROUND,
	image = IMAGES.SCIENCE.COMMON_BACKGROUND,
}) => {
	return (
		<LoaderWrapper>
			<LazyImage
				compressedImage={compressedImage}
				image={image}
				wrapperProps={{ className: "background-image" }}
			/>

			<div className="loader-content">
				<CircularProgress className="spinner" size={60} thickness={4} />
				<p className="loader-text">{message}</p>
			</div>
		</LoaderWrapper>
	);
};

export default GameLoader;
